import * as React from "react";
import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router";
import {css} from "@emotion/react";
import {Formik} from "formik";
import {forEach, isEmpty, isFinite, isPlainObject, map, reduce} from "lodash";
import {DatePicker} from "@web2/ap-datepicker";
import {FormFieldType, fromQueryValues, toQueryValues} from "@web2/ap-form";
import {appendQueryString, parseSearch} from "@web2/ap-request";
import {Select} from "@web2/ap-select";
import {Button} from "@web2/button";
import {getFormikFieldProps, IFormikSubmitFn} from "@web2/formik_utils";
import {Input} from "@web2/input";

import {IAPStore} from "../../app/reducers/ap_reducer";
import {tac} from "../../app/styles/helpers";
import {resetApplicationList} from "../actions/fetch_applicactions";
import {ApplicationSourceGroup} from "../reducers/applications_list_reducer";
import {SearchCriteria} from "./SearchCriteria";

const initialEmptyValues = {
    create_date: {
        lower: "",
        upper: ""
    },
    agent: null,
    source_group: "",
    name: "",
    phone: "",
    email: "",
    text: ""
};

const fieldTypes = {
    create_date: FormFieldType.InputRange,
    agent: FormFieldType.SelectString,
    source_group: FormFieldType.Select,
    name: FormFieldType.Input,
    phone: FormFieldType.Input,
    email: FormFieldType.Input,
    text: FormFieldType.Input
};

export const ApplicationSearchForm = () => {
    const [activeFiltersCount, setActiveFiltersCount] = useState(0);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const {sort, page} = parseSearch(location.search);

    const agentList = useSelector((store: IAPStore) =>
        map(store.applications.agentsList, (agent) => ({label: `${agent.name} ${agent.last_name}`, value: agent.agent_id.toString()}))
    );

    const sourceGroupOptions = [
        {
            label: "Formularz",
            value: ApplicationSourceGroup.FORM_APPLICATION
        },
        {
            label: "Połączenia telefoniczne",
            value: ApplicationSourceGroup.PHONE_APPLICATION
        }
    ];

    const onSubmit: IFormikSubmitFn<typeof initialEmptyValues> = (values, formikHelpers) => {
        setActiveFiltersCount(getActiveFiltersCount(values));
        formikHelpers.setSubmitting(false);
        const query = {
            sort,
            page,
            ...toQueryValues(fieldTypes, values)
        };
        history.push(appendQueryString(location.pathname, query));
    };

    const initialFormValues = {
        ...initialEmptyValues,
        ...fromQueryValues<typeof initialEmptyValues>(fieldTypes, parseSearch(location.search))
    };

    useEffect(() => {
        setActiveFiltersCount(getActiveFiltersCount(initialFormValues));
    }, [location]);

    return (
        <Formik initialValues={isEmpty(initialFormValues) ? initialEmptyValues : initialFormValues} onSubmit={onSubmit} enableReinitialize>
            {(formikProps) => (
                <form className="search-form" onSubmit={formikProps.handleSubmit}>
                    <SearchCriteria
                        fields={activeFiltersCount}
                        onReset={() => {
                            setActiveFiltersCount(0);
                            dispatch(resetApplicationList());
                            formikProps.setValues(initialEmptyValues);
                            history.push(location.pathname);
                        }}
                    />

                    <div css={formHolder}>
                        <DatePicker
                            {...getFormikFieldProps(formikProps, "create_date")}
                            onAfterChange={() => formikProps.submitForm()}
                            selectsRange
                            id="223"
                            label="Data zgłoszenia"
                            isClearable
                        />

                        <Select
                            {...getFormikFieldProps(formikProps, "agent")}
                            onAfterChange={() => formikProps.submitForm()}
                            options={agentList}
                            label="Agent"
                            searchable
                            clearable
                        />

                        <Select
                            {...getFormikFieldProps(formikProps, "source_group")}
                            onAfterChange={() => formikProps.submitForm()}
                            options={sourceGroupOptions}
                            label="Typ zgłoszenia"
                            clearable
                        />

                        <Input
                            {...getFormikFieldProps(formikProps, "name")}
                            onAfterChange={() => formikProps.submitForm()}
                            submitOnClear
                            label="Imię I nazwisko"
                        />

                        <Input {...getFormikFieldProps(formikProps, "phone")} onAfterChange={() => formikProps.submitForm()} submitOnClear label="Telefon" />

                        <Input {...getFormikFieldProps(formikProps, "email")} onAfterChange={() => formikProps.submitForm()} submitOnClear label="Email" />

                        <Input
                            {...getFormikFieldProps(formikProps, "text")}
                            onAfterChange={() => formikProps.submitForm()}
                            submitOnClear
                            label="Treść zapytania"
                        />
                    </div>

                    <div css={tac}>
                        <Button type="submit" variant="primary" disabled={formikProps.isSubmitting}>
                            Wyszukaj
                        </Button>
                    </div>
                </form>
            )}
        </Formik>
    );
};

const getActiveFiltersCount = (values: typeof initialEmptyValues) =>
    reduce(
        values,
        (acc: number, val) => {
            if (isPlainObject(val)) {
                forEach(val as {}, (date) => {
                    if (!isEmpty(date)) {
                        ++acc;
                        return false; // breaks forEach loop - we count objects (currently only date) as single criteria
                    }
                });
            } else if (typeof val === "string" && val) {
                return ++acc;
            } else if (isFinite(val)) {
                return ++acc;
            }
            return acc;
        },
        0
    );

const formHolder = css`
    padding: 1.5rem;
`;
